import { useEffect, useState } from "react";
import { STORY_HERO_OVERRIDES } from "@/data/heroOverrides.generated";

export interface StoryHeroOverride {
  url: string;
  alt: string;
}

const seedFor = (slug: string | undefined): StoryHeroOverride | null => {
  if (!slug) return null;
  const seed = STORY_HERO_OVERRIDES[slug];
  return seed ? { url: seed.url, alt: seed.alt ?? "" } : null;
};

/**
 * Fetch the admin-uploaded hero override for a happening story slug.
 * Returns `null` when no override exists, in which case callers should fall
 * back to the bundled hero image.
 *
 * Initial state is seeded from the build-time generated override map so the
 * correct hero paints immediately instead of swapping in after the fetch.
 */
export const useStoryHero = (slug: string | undefined) => {
  const [override, setOverride] = useState<StoryHeroOverride | null>(() => seedFor(slug));
  const [loading, setLoading] = useState(() => !seedFor(slug));

  useEffect(() => {
    if (!slug) {
      setOverride(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const seed = seedFor(slug);
    setOverride(seed);
    setLoading(!seed);
    // Load the backend client lazily so it doesn't delay the first paint.
    import("@/integrations/supabase/client").then(({ supabase }) => supabase
      .from("story_hero_overrides")
      .select("url, alt")
      .eq("story_slug", slug)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        const next: StoryHeroOverride | null =
          error || !data ? null : { url: data.url, alt: data.alt ?? "" };
        setOverride((prev) =>
          prev?.url === next?.url && prev?.alt === next?.alt ? prev : next,
        );
        setLoading(false);
      }));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return { override, loading };
};
